// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@adl/App/Store/Reducers";
import { getReorderPadDataViewProperty } from "@adl/App/Store/Selectors/ReorderPadSelectors";
import { xScreen } from "@adl/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {}

const mapStateToProps = (state: ApplicationState) => {
    const pagination = getReorderPadDataViewProperty(state, "pagination");
    const firstElementIndex = pagination ? (pagination.page - 1) * pagination.pageSize + 1 : 0;
    const lastElementIndex = pagination ? pagination.page * pagination.pageSize : 0;

    return {
        totalItemCount: pagination?.totalItemCount || 0,
        firstElementIndex,
        lastElementIndex,
    };
};

type Props = ReturnType<typeof mapStateToProps> & OwnProps;

export interface ReorderPadProductCountStyles {
    wrapper?: InjectableCss;
    tabletCountText?: TypographyPresentationProps;
    countText?: TypographyPresentationProps;
    itemsText?: TypographyPresentationProps;
}

export const productCountStyles: ReorderPadProductCountStyles = {
    wrapper: {
        css: css`
            margin-bottom: 8px;
            ${xScreen("lte", "md")} {
                margin-bottom: 0;
            }
        `,
    },
    tabletCountText: {
        size: 20,
        weight: "bold",
        css: css`
            white-space: nowrap;
            letter-spacing: 0;
            line-height: 25px;
        `,
    },
    countText: {
        size: 12,
        css: css`
            white-space: nowrap;
            letter-spacing: 0;
            line-height: 18px;
        `,
    },
    itemsText: {
        size: 20,
        weight: "bold",
        css: css`
            letter-spacing: 0;
            line-height: 25px;
        `,
    },
};

const styles = productCountStyles;

const ReorderPadProductCount: FC<Props> = ({ totalItemCount, firstElementIndex, lastElementIndex }) => {
    const isTabletScreen = useAppSelector(state => (state as CustomAppState).xCommon.isTabletScreen);
    const itemsText = totalItemCount !== 1 ? " products" : " product";

    return (
        <StyledWrapper {...styles.wrapper}>
            {isTabletScreen ? (
                <Typography {...styles.tabletCountText} data-test-selector="productListCountText">
                    {totalItemCount} {itemsText}
                </Typography>
            ) : (
                <Typography {...styles.countText} data-test-selector="productListCountText">
                    {translate("showing")} {firstElementIndex} -{" "}
                    {lastElementIndex > totalItemCount ? totalItemCount : lastElementIndex} {translate("of")}{" "}
                    {totalItemCount} {itemsText}
                </Typography>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps)(ReorderPadProductCount);
